'use client'

/**
 * Shared context for the HOME INFLUENCER_V2 widget-of-widget pair.
 *
 * INFLUENCER_SECTION is the container: it owns the list-level state the legacy `InfluencerV2Legacy`
 * section keeps internally — which card's video is playing, the shared mute flag, the batched
 * product fetch and the shoppable popup — and arranges the pre-rendered INFLUENCER_CARD children.
 * Those concerns can't live on an individual card, so the section publishes them here, bound
 * per-card (the nested INFLUENCER_CARD widgets ship empty ids, so an id lookup can't locate them);
 * each card widget reads its slot and renders the existing `InfluencerV2Card` with them.
 */

import { createContext, useContext } from 'react'

import type { TrackingMetaData } from '@src/quince-site-common/src/domains/CustomSection'

export interface InfluencerV2CardContextValue {
  /** The card's index within the section's shoppable strip (drives play state + popup position). */
  index: number
  isPlaying: boolean
  isMuted: boolean
  playVideo: (index: number) => void
  stopVideo: () => void
  muteVideo: () => void
  trackingMetaData: TrackingMetaData
  tracker: string
  /** Section's card click: PRODUCT_CLICK telemetry + shoppable popup at this card's strip index. */
  onCardClick: (index: number) => void
}

const InfluencerV2CardContext = createContext<InfluencerV2CardContextValue | null>(null)

export const InfluencerV2CardContextProvider = InfluencerV2CardContext.Provider

export const useInfluencerV2CardContext = (): InfluencerV2CardContextValue | null => useContext(InfluencerV2CardContext)
